/**
 * **URL: `/` exactly.** The index route of the `_app` layout, so it renders inside that
 * layout's chrome rather than replacing it.
 *
 * `/` is a landing spot, not a page. This file owns one decision — **which garden to
 * open** — and the two states in which it cannot make it yet or at all:
 *
 * | State | Renders |
 * |---|---|
 * | garden list still loading | a bars loader |
 * | the user has no gardens | an empty state offering to create one |
 * | otherwise | `<Navigate replace>` to the remembered garden's journal |
 *
 * The target is the last garden viewed (`readLastGardenToken()`), falling back to the
 * first, and the last tab used (`readLastTab()`). Both are read once here rather than
 * subscribed to: this is a bounce, not a page.
 *
 * **It is the only owner of that decision.** `_app.tsx`'s `currentGarden` is the garden the
 * URL *names* and is `undefined` here, so nothing else can disagree about where `/` goes.
 */

import { Button } from '@mantine/core';
import { createFileRoute, Navigate, useNavigate } from '@tanstack/react-router';
import { useQuery } from 'convex-helpers/react/cache';
import { useConvex, useConvexAuth } from 'convex/react';

import { api } from '../../convex/_generated/api';
import { FullPageLoader } from '../components/FullPageLoader';
import { FullPageMessage } from '../components/FullPageMessage';
import { openAddGardenModal } from '../components/GardenDialog';
import { readLastTab } from '../lib/gardenTabs';
import { readLastGardenToken } from '../lib/lastGarden';

/**
 * Why the decision lives in a component here, rather than either of the two places it
 * looks like it belongs:
 *
 * - **Not an effect on the `_app` layout.** That is where it used to be, and it fired
 *   once — when the garden list first loaded — leaving every later arrival at `/` on a
 *   dead spinner. Here it is re-evaluated every time we are actually at `/`.
 * - **Not a `beforeLoad` redirect.** The garden list is a Convex subscription owned by
 *   React, so a loader would have to block on a one-shot query before first paint.
 */
export const Route = createFileRoute('/_app/')({
  component: function IndexComponent() {
    const convex = useConvex();
    const navigate = useNavigate();
    const { isAuthenticated } = useConvexAuth();
    // deduped against the same subscription in the _app layout
    const gardens = useQuery(api.gardens.listGardens, isAuthenticated ? {} : "skip");

    if (!gardens) {
      return <FullPageLoader />;
    }

    if (gardens.length === 0) {
      return (
        <FullPageMessage
          title="No gardens yet"
          body="Create a garden to start keeping a journal."
          action={
            <Button
              variant="light"
              onClick={() => openAddGardenModal(convex, async (garden) => {
                await navigate({
                  to: `/g/$gardenToken/${readLastTab()}`,
                  params: { gardenToken: garden.token },
                  replace: true,
                });
              })}
            >
              Create a garden
            </Button>
          }
        />
      );
    }

    // the remembered garden may have been deleted or unshared since, so fall
    // back to the first one rather than trusting the stored token
    const lastToken = readLastGardenToken();
    const garden = gardens.find((g) => g.token === lastToken) ?? gardens[0];

    // replace: `/` is a bounce and should not sit in the history stack
    return <Navigate
      replace
      to={`/g/$gardenToken/${readLastTab()}`}
      params={{ gardenToken: garden.token }}
    />;
  },
})
